import React, { useState, useContext, useMemo, useReducer, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { demoContext } from "../../../../shared/store/contexts/context.demo";

import * as Icon from "react-feather";
import moment from "moment";

import referralsPieIcon from "../../../../assets/images/pie-three.svg";
import totalCasesPieIcon from "../../../../assets/images/chart-pie.svg";
import eyesIcon from "../../../../assets/images/eyes.svg";
import trendIcon from "../../../../assets/images/trend.svg";
import positiveDynamicsIcon from "../../../../assets/images/positive-dynamics.svg";

import ActionPageHero from "../../../molecules/ActionPageHero";
import { PrimaryButton } from "../../../atoms/Buttons";
import { H3, H6, P } from "../../../atoms/Texts";
import { Pill } from "../../../atoms/Badges/badge.ssn";
import pillStylesEnum from "../../../../shared/fixtures/pillStylesEnum";
import { Table } from "../../../organisms/Table";

import { exportToCSV, getDailyData } from "../../../../shared/helpers";

import { countItemsByValue } from "../../../../shared/helpers/util.countItemsByValue";
import SSNPieChart from "../../../organisms/PieChart";
import SSNLineChart from "../../../organisms/LineChart";

import Joyride from "react-joyride";
import { useWalkthrough } from "../../../../shared/hooks/useWalkthrough";

const ImpactManagementPage = () => {
  const steps = [
    {
      target: ".total-referrals",
      content: "This shows the numbers of refererrals received.",
    },
    {
      target: ".total-cases",
      content: "This shows the number of cases your organisation currently have.",
    },
    {
      target: ".total-services",
      content: "This is the total number of services your organisation offers.",
    },

    {
      target: ".closed-cases",
      content: "Total number of successfully closed cases.",
    },
    {
      target: ".org-saves",
      content: "This shows how many users have saved your organisation.",
    },
    {
      target: ".org-views",
      content: "This shows the number of times your organisation has been viewed.",
    },
    {
      target: ".org-graph",
      content:
        "A monthly based graphical representation of organisation view, saves and referrals",
    },
    {
      target: ".export",
      content: "Export impact data for further analysis.",
    },
    {
      target: ".referral-sources",
      content: "Displays the different referral sources.",
    },

    {
      target: ".all-cases",
      content: "View all cases.",
    },
  ];

  const { joyrideProps } = useWalkthrough({
    steps,
    walkthroughKey: "impact_page_data",
    pageSpecific: true, // Each page tracks separately
  });

  const [currentPage, setCurrentPage] = useState(0);
  const { id } = useParams();

  const { organization, referrals, impactMetrics, orgAnalyticsData, cases } =
    useContext(demoContext);

  // Get referrals by source
  const getReferralForSource = (source) => {
    return referrals?.filter((referral) => referral.from === source);
  };

  const impact = useMemo(() => {
    return impactMetrics?.data?.getImpactMetrics;
  }, [impactMetrics]);

  const [orgAnalytics, dispatch] = useReducer(analyticsReducer, {});

  useMemo(() => {
    if (orgAnalyticsData) {
      const analytics = orgAnalyticsData?.getOrgAnalytics;

      const orgSavesByGender = analytics?.numberOfSaves;

      const orgEventDates = getDailyData(
        analytics?.viewsDates,
        analytics?.saveDates,
        analytics?.referDates,
      );
      dispatch({ prop: "orgSavesByGender", value: orgSavesByGender });
      dispatch({ prop: "orgEventDates", value: orgEventDates });
    }
  }, [orgAnalyticsData]);

  useMemo(() => {
    if (referrals?.getAllReferralsByOrgID) {
      const referralsByStatus = countItemsByValue(
        referrals?.getAllReferralsByOrgID,
        "status",
      );
      const referralsByServiceName = countItemsByValue(
        referrals?.getAllReferralsByOrgID,
        "serviceName",
      );
      dispatch({ prop: "referralsByServiceName", value: referralsByServiceName });
      dispatch({ prop: "referralsByStatus", value: referralsByStatus });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrals?.getAllReferralsByOrgID]);

  useMemo(() => {
    if (cases) {
      const casesByStatus = countItemsByValue(cases, "status");
      dispatch({ prop: "casesByStatus", value: casesByStatus });
    }
  }, [cases]);

  return (
    <>
      <Joyride
        key={joyrideProps.run ? "walkthrough-active" : "walkthrough-inactive"}
        {...joyrideProps}
      />

      <main className="flex flex-col w-full">
        <div className="flex justify-between items-center">
          <ActionPageHero
            className="organization-container"
            title="Impact"
            description="Track impact of services"
            action
            actionComponent={
              <PrimaryButton
                className="export"
                onClick={() =>
                  exportToCSV({
                    orgName: organization?.name,
                    orgAnalytics,
                    cases,
                  })
                }
              >
                Export Impact Data
              </PrimaryButton>
            }
          />
        </div>

        <section className="organization-container pb-10 flex-grow flex flex-col mt-6">
          {/* Overciew */}
          <H6 fontSize="18px" className="mb-3" fontFamily="regular">
            Overview
          </H6>

          {/* Overview cards */}
          <div className="grid grid-cols-4 gap-x-5">
            {/* Referrals card */}
            <div className=" total-referrals p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={referralsPieIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Referrals
                </P>
              </div>

              <H3 color="#22202D">{impact?.numberOfReferralByOrgId}</H3>
            </div>

            {/* Total cases card */}
            <div className="total-cases p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={totalCasesPieIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Total Cases
                </P>
              </div>

              <H3 color="#22202D">{impact?.numberOfCasesByOrgId}</H3>
            </div>

            {/* Services card */}
            <div className="total-services p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={eyesIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Services
                </P>
              </div>

              <H3 color="#22202D">{impact?.numberOfServicesByOrgId}</H3>
            </div>

            {/* Closed cases card */}
            <div className=" closed-cases p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={trendIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Closed cases
                </P>
              </div>

              <H3 color="#22202D">{impact?.numberOfClosedCasesByOrgId}</H3>
            </div>
          </div>

          {/* Organization cards */}
          <div className="grid grid-cols-4 gap-x-5 mt-5">
            {/* Saves count card */}
            <div className="org-saves p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={positiveDynamicsIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Organization Saves
                </P>
              </div>

              <H3 color="#22202D">
                {orgAnalyticsData?.getOrgAnalytics?.numberOfSaves}
              </H3>
            </div>

            {/* Views count card */}
            <div className="org-views p-5 h-32 shadow-sm rounded-lg bg-white">
              <div className="flex items-center mb-2">
                <img src={positiveDynamicsIcon} alt="" className="w-4 h-4 mr-3" />
                <P color="#7D7B96" fontSize="14px">
                  Organization Views
                </P>
              </div>

              <H3 color="#22202D">
                {orgAnalyticsData?.getOrgAnalytics?.numberOfViews}
              </H3>
            </div>
          </div>

          {/* Heartbeat Metrics */}
          <section className="grid grid-cols-3 mt-10 gap-x-4">
            <div className="col-span-3 h-full flex flex-col ">
              <H6 fontSize="18px" className="mb-3" fontFamily="regular">
                Organization Metrics
              </H6>

              <div className="org-graph bg-white px-4 pr-6 py-5">
                {orgAnalytics?.orgEventDates?.length > 0 ? (
                  <SSNLineChart data={orgAnalytics?.orgEventDates} />
                ) : (
                  <div className="py-4 text-center">
                    <H6>There is no data for this yet!</H6>
                    <P>We will continue tracking your organization's engagements.</P>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Referral Pie Charts */}
          <section className="mt-10 grid grid-cols-3 gap-x-5">
            {orgAnalytics?.referralsByStatus && (
              <div className="h-full flex flex-col ">
                <H6 fontSize="18px" className="mb-3" fontFamily="regular">
                  Referrals by status
                </H6>
                <div className="flex flex-col flex-grow">
                  <div className="bg-white px-4 pr-6 py-5 flex-grow">
                    {orgAnalytics?.referralsByStatus && (
                      <SSNPieChart
                        height={200}
                        data={orgAnalytics.referralsByStatus}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {orgAnalytics?.referralsByServiceName && (
              <div className="h-full flex flex-col justify-self-stretch ">
                <H6 fontSize="18px" className="mb-3" fontFamily="regular">
                  Referrals by Service
                </H6>
                <div className="flex flex-col flex-grow">
                  <div className="bg-white px-4 pr-6 py-5 flex-grow">
                    {orgAnalytics?.referralsByServiceName && (
                      <SSNPieChart
                        height={200}
                        data={orgAnalytics.referralsByServiceName}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {orgAnalytics?.casesByStatus && (
              <div className="h-full flex flex-col justify-self-stretch ">
                <H6 fontSize="18px" className="mb-3" fontFamily="regular">
                  Cases Profile
                </H6>
                <div className="flex flex-col flex-grow">
                  <div className="bg-white px-4 pr-6 py-5 flex-grow">
                    {orgAnalytics?.casesByStatus && (
                      <SSNPieChart height={200} data={orgAnalytics.casesByStatus} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Referral Sources and All cases */}
          <section className="flex mt-10">
            <div className="w-1/3 h-full flex flex-col pr-5">
              <H6 fontSize="18px" className="mb-3" fontFamily="regular">
                Referral Sources
              </H6>
              <div className="referral-sources bg-white p-8 pl-6 rounded shadow-sm space-y-8 flex-grow">
                {referrals?.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 mr-4"
                          style={{ background: "#6079E4" }}
                        />
                        <P color="#413E53">Social Safety Net</P>
                      </div>
                      <div className="flex items-center">
                        <P color="#000000">{getReferralForSource("ssn")?.length}</P>

                        <img
                          src={positiveDynamicsIcon}
                          alt=""
                          className="w-4 h-4 ml-2"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 mr-4"
                          style={{ background: "#6079E4" }}
                        />
                        <P color="#413E53">SDEP</P>
                      </div>
                      <div className="flex items-center">
                        <P color="#000000">{getReferralForSource("sdep")?.length}</P>

                        <img
                          src={positiveDynamicsIcon}
                          alt=""
                          className="w-4 h-4 ml-2"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 mr-4"
                          style={{ background: "#6079E4" }}
                        />
                        <P color="#413E53">Form</P>
                      </div>
                      <div className="flex items-center">
                        <P color="#000000">{getReferralForSource("form")?.length}</P>

                        <img
                          src={positiveDynamicsIcon}
                          alt=""
                          className="w-4 h-4 ml-2"
                        />
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <Link to={`/dashboard/organization/${id}/referrals`}>
                        <PrimaryButton.Light>Details</PrimaryButton.Light>
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex justify-center flex-col">
                    <H3 fontSize="16px" className="mb-4">
                      No referrals
                    </H3>
                    <P className="">
                      Your organization doesn't have any referrals at the moment.
                    </P>
                  </div>
                )}
              </div>
            </div>

            {/* All cases card */}
            <div className="w-2/3 h-full flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <H6 fontSize="18px" className="" fontFamily="regular">
                  All cases
                </H6>
                <Link to={`/dashboard/organization/${id}/case-management`}>
                  <P color="#5F5D77" fontSize="14px">
                    View All
                  </P>
                </Link>
              </div>
              <div className="all-cases bg-white p-8 pl-6 rounded shadow-sm space-y-8 flex-grow">
                <div className="border-gray-300 border border-b-0 border-l-0 border-r-0 border-t h-full">
                  {cases?.length > 0 ? (
                    <Table
                      columns={columns}
                      data={cases}
                      loading={false}
                      empty_sub_message="Add cases you want to track quickly "
                      current_page={currentPage}
                      setCurrentPage={setCurrentPage}
                      noHeader
                      noFooter
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center flex-col py-10">
                      <H3 className="mb-1">No cases</H3>
                      <p className="">
                        Your organization doesn't have any cases at the moment.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </section>
      </main>
    </>
  );
};

export default ImpactManagementPage;

const analyticsReducer = (state, action) => {
  switch (action.prop) {
    case "casesByStatus":
      return {
        ...state,
        casesByStatus: action.value,
      };

    case "referralsByStatus":
      return {
        ...state,
        referralsByStatus: action.value,
      };

    case "referralsByServiceName":
      return {
        ...state,
        referralsByServiceName: action.value,
      };

    case "orgSavesByGender":
      return {
        ...state,
        orgSavesByGender: action.value,
      };

    case "orgEventDates":
      return {
        ...state,
        orgEventDates: action.value,
      };

    default:
      break;
  }
};

const More = ({ serviceId }) => {
  const { id: orgId } = useParams();

  const [showMore, setShowMore] = useState(false);

  const toggleDropDown = () => setShowMore(!showMore);

  const checkIfNotInDropDown = ({ target }) => {
    if (!target.classList.contains("more-dropdown-child")) {
      toggleDropDown();
    }
  };

  useEffect(() => {
    if (showMore) {
      window.addEventListener("click", checkIfNotInDropDown);
    } else {
      window.removeEventListener("click", checkIfNotInDropDown);
    }

    return () => {
      window.removeEventListener("click", checkIfNotInDropDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMore]);

  return (
    <div className="relative">
      <Icon.MoreVertical
        size={24}
        color="#C5C7CD"
        className="cursor-pointer"
        onClick={toggleDropDown}
      />

      {showMore && (
        <div className="absolute z-50 top-3 right-0 bg-white shadow py-6 px-4 more-dropdown-child ">
          <Link
            to={`/dashboard/organization/${orgId}/case-management/${serviceId}`}
            className="flex items-center whitespace-no-wrap"
          >
            <P className="whitespace-no-wrap flex-shrink-0 more-dropdown-child"></P>
            View Case <Icon.ExternalLink className="ml-2 " size={16} />
          </Link>
        </div>
      )}
    </div>
  );
};

const columns = [
  {
    accessor: "referral.id",
    Header: "",
    Cell: ({ row }) => (
      <P color="#252733" className=" flex items-center">
        {row.index + 1}
      </P>
    ),
  },
  {
    accessor: "referral",
    Header: "",
    Cell: ({ value }) => (
      <P color="#252733" className=" flex items-center">
        {value?.user?.firstName} {value?.user?.lastName}
        {/* <Link to={`/dashboard/customer-detail/${value}`}>
          <span className="sm:text-sm ml-1 inline-block">
            <GoLinkExternal />
          </span>
        </Link> */}
      </P>
    ),
  },
  {
    accessor: "caseNumber",
    Header: "",
    Cell: ({ value }) => (
      <P color="#252733" className="">
        {value}
      </P>
    ),
  },
  {
    accessor: "referral.from",
    Header: "",
    Cell: ({ value }) => (
      <Pill
        color={pillStylesEnum[value?.toUpperCase()].color}
        bg={pillStylesEnum[value?.toUpperCase()].bg}
        fontSize="12px"
      >
        {value?.toUpperCase()}
      </Pill>
    ),
  },
  {
    accessor: "createdAt",
    Header: "",
    Cell: ({ value }) => (
      <div>
        <p className="">{moment(value).format("ll")}</p>
        <P color="#C5C7CD" className="">
          {moment(value).format("LT")}
        </P>
      </div>
    ),
  },
  {
    accessor: "id",
    Header: "",
    Cell: ({ value }) => (
      <span className="">
        <More serviceId={value} />
      </span>
    ),
  },
];
